// Create-or-join and the invitation gate (docs/FILE_SPLIT_RUN1_SPEC.md, run 1): InvitationGate and
// Onboarding stay here; the PIN pad, recovery, Help, push, device-role, install and profile-switcher
// pieces now live under ./onboarding/ and are re-exported unchanged below.

import { CoinIcon, CopyrightFooter, Icon, LittleBankersHeaderMark, VaultIcon } from "./Common.jsx";
import { track } from "../lib/metrics.js";
import { apiOwnerCheck, apiOwnerRecap } from "../lib/api.js";
import { DEVICE_CODE_KEY, OWNER_NAME_MAX, cleanOwnerName } from "../lib/constants.js";
import { cameraSupported } from "../lib/qrscan.js";
import { joinTicketLink } from "../lib/qrcode.js";
import { t as ct } from "../lib/content.js";
import { publicPageHref } from "../lib/origin.js";
import { PasscodeRecoveryRequest } from "./onboarding/Recovery.jsx";
import { InstallExplainerCard } from "./onboarding/Install.jsx";
import { QrScanOverlay } from "./onboarding/QrScanOverlay.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// notice (LB-184): true when the person was sent back here because their creation ticket had
// expired or was already spent — the gate says so in one neutral line above the usual copy.
export function InvitationGate({ value, onChange, onSubmit, error, busy, onBack, onWaitlist, notice }) {
  return (
    // Flex-column shell with the card centered in the flexible middle and the footer as a
    // normal (non-absolute) last child. Absolute-positioning the footer against this
    // min-h-screen container used to clip it on shorter viewports and let it sit on top of
    // the card's CTA instead of pushing it up — flow layout can't do either.
    <div className="relative min-h-screen min-h-[100dvh] flex flex-col p-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }}>
      <div className="flex-1 flex items-center justify-center">
        <div className="bg-white rounded-lg border border-slate-200 p-6 w-full max-w-sm md:max-w-[560px] text-center">
          <div className="mb-3 flex justify-center">
            <LittleBankersHeaderMark />
          </div>
          <div className="font-bold text-slate-800 mb-1">{ct("onboarding.invite.heading", "You're invited")}</div>
          <p className="text-xs text-slate-500 mb-4">{ct("onboarding.invite.subheading", "Little Bankers is invite-only. Enter your invitation code to continue.")}</p>
          {notice && <div className="bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg px-3 py-2 mb-3" data-testid="gate-notice">{ct("onboarding.invite.expired", "Your invitation has expired. Enter your code again.")}</div>}
          {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2 mb-3">{error}</div>}
          <input
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-3 text-center tracking-widest font-bold uppercase"
            placeholder="Invitation code"
            value={value}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && !busy && onSubmit()}
          />
          <button disabled={busy} className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5 disabled:opacity-50" onClick={onSubmit}>{busy ? "Checking…" : "Continue"}</button>
          {/* W3.5 fix: reached from the landing page, the gate needs a way back — without
              this (and the history entry Root pushes) a visitor with no code was stuck.
              Rendered only when a back destination exists, so nothing changes for any
              other route to the gate. */}
          {/* LB-95/97 — the way out. Someone who reaches this screen without a code, or
              whose code won't validate, previously had only "← Back" to the marketing
              page they'd already decided against. The waitlist is the one thing that can
              actually get them a code, so it belongs here, and the copy changes after a
              failed attempt because that is where the need is sharpest: "haven't got one"
              and "the one I've got doesn't work" are different problems with the same
              answer. Rendered whenever a route out exists, one line above Back so the
              useful exit reads first and the muted one second. */}
          {onWaitlist && (
            <div>
              <button className="mt-3 text-xs font-bold text-slate-900 underline underline-offset-2" data-testid="gate-waitlist" onClick={onWaitlist}>
                {error
                  ? ct("onboarding.invite.code_not_working", "Code not working? Join the waitlist")
                  : ct("onboarding.invite.no_code", "Haven't got a code? Join the waitlist")}
              </button>
            </div>
          )}
          {onBack && (
            <button className={`${onWaitlist ? "mt-2" : "mt-3"} text-xs font-bold text-slate-400 underline underline-offset-2`} data-testid="gate-back" onClick={onBack}>
              {ct("onboarding.invite.back", "Back")}
            </button>
          )}
        </div>
      </div>
      <CopyrightFooter />
    </div>
  );
}


export function Onboarding({ onStart, onJoin, onRestore, busy, error, pendingQrJoin, onDismissQrJoin, initialTab = "start", onWaitlist, joinOnly = false, adultInvite = null }) {
  // joinOnly (staging bug 1, 30 Aug 2026): this screen is also reached from the landing
  // page's "I've already got a code" route, which deliberately does NOT set the invite pass
  // — a returning parent proves themselves with household code + passcode instead. The New
  // tab was still sitting right there, so one tap created a brand-new household with no
  // invite code at all, which is the whole gate bypassed. In this mode the New tab does not
  // render and `tab` can never be "start".
  const [tab, setTab] = useState(joinOnly ? "join" : initialTab);
  // Handover fallback (Sep 2026): a resumed install whose ticket was dead (expired after 24h,
  // already used, or the bookmark reopened) still carries the household code in localStorage
  // — Root.jsx wrote it before stripping the URL. So the join screen starts with it filled in
  // and asks only for the passcode. `resumedCode` is what tells the screen to say so.
  const resumedCode = (() => { try { return localStorage.getItem(DEVICE_CODE_KEY) || ""; } catch { return ""; } })();
  const [joinCode, setJoinCode] = useState(resumedCode);
  const [joinPasscode, setJoinPasscode] = useState("");
  const [showJoinPasscode, setShowJoinPasscode] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState("");
  // LB-337: the owner's first name, asked for beside the email because this is the one
  // moment we already have the owner's attention on who they are. Required, trimmed, 40
  // characters, and no other rule — a name is not something to be told you got wrong
  // (principle 5), so there is no pattern to fail and no inline error to render.
  const [ownerName, setOwnerName] = useState("");
  const [agreedToLegal, setAgreedToLegal] = useState(false);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail.trim());
  const nameValid = !!cleanOwnerName(ownerName);

  // Email-ownership fork (review 2.2): owner-check runs on Create tap, BEFORE calling
  // onStart, so an existing owner sees the fork card instead of silently starting a
  // second household. checkingOwner covers the busy state on the Create button itself.
  const [checkingOwner, setCheckingOwner] = useState(false);
  const [showFork, setShowFork] = useState(false);
  const [recapSent, setRecapSent] = useState(false);

  async function handleCreateTap() {
    const email = ownerEmail.trim();
    setCheckingOwner(true);
    try {
      const exists = await apiOwnerCheck(email);
      if (exists) {
        track("create_fork_shown", {});
        setShowFork(true);
      } else {
        onStart(email, cleanOwnerName(ownerName));
      }
    } catch {
      // Fail OPEN, always — an owner-check outage must never block household creation.
      onStart(email, cleanOwnerName(ownerName));
    } finally {
      setCheckingOwner(false);
    }
  }

  async function handleForkRecap() {
    track("create_fork_choice", { kind: "recap" });
    setRecapSent(true);
    try { await apiOwnerRecap(ownerEmail.trim()); } catch {}
  }
  function handleForkRecover() {
    track("create_fork_choice", { kind: "recover" });
    setShowFork(false);
    setTab("join");
    setShowRecovery(true);
  }
  function handleForkCreateAnyway() {
    track("create_fork_choice", { kind: "create_anyway" });
    setShowFork(false);
    onStart(ownerEmail.trim(), cleanOwnerName(ownerName));
  }

  // In-app QR scanning (Aug 2026): `scanning` shows the camera overlay; a successful scan
  // lands its parsed payload in `scannedJoin`. This is the same {code, token} shape as
  // `pendingQrJoin` (a QR scanned by the phone's NATIVE camera, arriving via URL params in
  // Root.jsx) — kept as separate state deliberately so Root doesn't need to know or care
  // that an in-app scanner exists; both feed the identical confirmation card + onJoin(...,
  // true) token path below.
  const [scanning, setScanning] = useState(false);
  const [scannedJoin, setScannedJoin] = useState(null);
  const qrJoin = pendingQrJoin || scannedJoin;
  // LB-241: `error` is Root's onboarding error and outlives the reason it was shown — a
  // device removed from a household carries "This device was removed…" onto the very Join
  // screen it is now rejoining from, and it was still showing on the QR confirmation card.
  // Remember what the error said at the moment a QR arrived and don't repeat THAT text on
  // the card; a new error from this join attempt (different text) still shows.
  const [errorAtScan, setErrorAtScan] = useState(null);
  useEffect(() => { if (qrJoin) setErrorAtScan(error || null); }, [qrJoin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Install explainer (Aug 2026): a dismissible card at the top of the pitch screen
  // nudging install BEFORE create/join — the cleanest possible flow on iOS, since a
  // household born inside the installed app's own sandbox never hits the Safari-vs-
  // standalone storage divergence that ?resume= exists to patch. "No thanks" is
  // remembered per-device forever; storage errors default to dismissed (never nag a
  // device we can't remember the answer on). The card additionally hides itself whenever
  // install isn't actually offerable right now — see InstallExplainerCard below.
  const [installExplainerDismissed, setInstallExplainerDismissed] = useState(() => {
    try { return localStorage.getItem("lb_install_explainer_dismissed") === "1"; } catch { return true; }
  });
  function dismissInstallExplainer() {
    try { localStorage.setItem("lb_install_explainer_dismissed", "1"); } catch {}
    setInstallExplainerDismissed(true);
  }

  // A quick-join QR — whether scanned by the phone's native camera (pendingQrJoin, via URL
  // params) or by the in-app scanner (scannedJoin, above) — replaces the whole pitch/tabs
  // screen below with one focused confirmation. Code+token are already in hand, so there's
  // nothing to type, just a single deliberate tap (never an automatic join with no
  // confirmation at all) plus an escape hatch back to the ordinary manual Join tab.
  if (qrJoin) {
    return (
      <div className="relative min-h-screen min-h-[100dvh] flex flex-col p-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }}>
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-white rounded-lg border border-slate-200 p-6 w-full max-w-sm md:max-w-[560px] text-center">
            <div className="mb-3 flex justify-center"><LittleBankersHeaderMark /></div>
            <div className="text-lg font-extrabold text-slate-900 mb-1">{ct("onboarding.qrjoin.heading", "Quick join")}</div>
            <p className="text-sm text-slate-500 mb-4">Join household <b className="text-slate-800 tracking-widest">{qrJoin.code}</b> on this device?</p>
            {error && error !== errorAtScan && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2 mb-3 text-left">{error}</div>}
            <button disabled={busy} className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 disabled:opacity-50 mb-2" onClick={() => onJoin(qrJoin.code, qrJoin.token, true)}>
              {busy ? "Joining…" : "Join now"}
            </button>
            <button className="w-full bg-white border border-slate-200 text-slate-500 font-bold text-sm rounded-xl py-2.5" onClick={() => { setScannedJoin(null); if (pendingQrJoin) onDismissQrJoin(); }}>Not now — enter manually</button>
          </div>
        </div>
        <CopyrightFooter />
      </div>
    );
  }

  // Full-screen camera overlay while scanning. On a successful decode it drops straight
  // into the scannedJoin confirmation above; ✕ (or a camera failure's Back button)
  // returns to the Join tab with everything intact.
  if (scanning) {
    return (
      <QrScanOverlay
        onScanned={(payload) => {
          setScanning(false);
          // A TICKET earns a pending request, not a join, so it cannot use the confirmation
          // card below (which calls onJoin with a standing token). Send the app to the very
          // address the phone's own camera would have opened: Root's boot parses it, passes
          // the invite gate on it (readEntryParams: `if (validTicketJoin)`) and shows the
          // waiting-for-approval screen. One parser, one flow, and the two ways of scanning
          // become the same journey rather than two that have to be kept in step.
          //
          // Rebuilt from the validated parts on OUR origin, never the scanned text: the
          // parser above ignores the QR's hostname on purpose, so navigating to what was
          // scanned would follow a stranger's URL.
          if (payload.kind === "ticket") {
            window.location.assign(joinTicketLink(payload.code, payload.ticket));
            return;
          }
          setScannedJoin(payload);
        }}
        onClose={() => setScanning(false)}
      />
    );
  }

  return (
    // Same flex-column shell as InvitationGate — card centered in the flexible middle,
    // footer as a normal last child so it can never overlap the Create/Join CTA below the
    // card or get clipped on a viewport shorter than this tab's content.
    <div className="relative min-h-screen min-h-[100dvh] flex flex-col p-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }}>
      <div className="flex-1 flex items-center justify-center">
      <div className="bg-white rounded-lg border border-slate-200 p-6 w-full max-w-sm md:max-w-[560px]">
        <div className="mb-3 flex justify-center">
          <LittleBankersHeaderMark />
        </div>
        {/* adultInvite added 20 Sep. Someone following an emailed invite with no app installed
            lands HERE, on the pitch, with this sheet over the top — so this is the first
            install an invited adult is offered, and a bare button carries a creation pass
            they do not need instead of the invite they are holding. Bare still means
            pre-household, which is what an ordinary visitor to this screen is. */}
        {!installExplainerDismissed && <InstallExplainerCard adultInvite={adultInvite} onDismiss={dismissInstallExplainer} />}
        <h1 className="text-lg font-extrabold text-slate-900 text-center mb-1">{ct("onboarding.welcome.heading", "Where kids learn the value of money")}</h1>
        <p className="text-xs text-slate-500 text-center mb-4">{ct("onboarding.welcome.subheading", "Kids earn, save and spend from their own space. You set it up and approve every step.")}</p>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-slate-50 rounded-lg px-2 py-2.5 text-center">
            <CoinIcon className="w-5 h-5 mx-auto" />
            <div className="text-xs font-semibold text-slate-700 mt-1">{ct("onboarding.pillar_earn", "Earn")}</div>
            <div className="text-xs text-slate-400 leading-snug">{ct("onboarding.pillar_earn_sub", "from helping out, learning, good behaviour.")}</div>
          </div>
          <div className="bg-slate-50 rounded-lg px-2 py-2.5 text-center">
            <div className="leading-none flex justify-center" aria-hidden="true"><Icon id="ic-goals" size={20} hue="indigo" /></div>
            <div className="text-xs font-semibold text-slate-700 mt-1">{ct("onboarding.pillar_save", "Save")}</div>
            <div className="text-xs text-slate-400 leading-snug">{ct("onboarding.pillar_save_sub", "toward goals and experiences.")}</div>
          </div>
          <div className="bg-slate-50 rounded-lg px-2 py-2.5 text-center">
            <VaultIcon className="w-5 h-5 mx-auto" />
            <div className="text-xs font-semibold text-slate-700 mt-1">{ct("onboarding.pillar_grow", "Grow")}</div>
            <div className="text-xs text-slate-400 leading-snug">{ct("onboarding.pillar_grow_sub", "savings and financial confidence.")}</div>
          </div>
        </div>

        {/* Was one 👪 beside a plain bulleted list. Now each line carries its own sprite
            icon, which does more work than a shared emoji: the icon identifies WHICH of
            the three the line is about (kid side / parent side / sync), and it matches the
            marks the same concepts use once you're inside the app. Copy is CMS-driven so
            these can be tuned without a deploy — the fallbacks here are the shipped words,
            unchanged. Icons stay in code on purpose: they're part of the layout, and
            letting CMS pick a sprite id would let a bad edit render an empty <use>. */}
        <div className="border-t border-b border-slate-200 py-2.5 mb-4">
          <ul className="text-xs text-slate-700 leading-relaxed space-y-1.5">
            {[
              ["ic-kids", ct("onboarding.gate.point_kids", "Kids do the tracking themselves within Kid Mode.")],
              ["ic-gear", ct("onboarding.gate.point_parent", "You keep full visibility and control from Parent Mode.")],
              ["ic-devices", ct("onboarding.gate.point_sync", "Sync the whole household across as many devices as you want.")],
            ].map(([icon, line]) => (
              <li key={icon} className="flex items-start gap-2">
                <Icon id={icon} size={14} hue="teal" className="flex-shrink-0 mt-0.5" />
                <span className="flex-1 min-w-0">{line}</span>
              </li>
            ))}
          </ul>
        </div>

        {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2 mb-3">{error}</div>}

        {!joinOnly && (
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-4">
            <button onClick={() => setTab("start")} className={`flex-1 rounded-lg py-2 text-xs font-bold ${tab === "start" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{ct("onboarding.tab_new", "New")}</button>
            <button onClick={() => setTab("join")} className={`flex-1 rounded-lg py-2 text-xs font-bold ${tab === "join" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{ct("onboarding.tab_join", "Join")}</button>
          </div>
        )}
        {joinOnly && (
          <p className="text-xs text-slate-500 mb-4" data-testid="join-only-note">
            {ct("onboarding.join_only_note", "Signing in to a household you already have. Starting a new one needs an invitation code.")}
            {onWaitlist && (
              <button className="text-emerald-600 font-bold ml-1 underline underline-offset-2" onClick={onWaitlist}>
                {ct("onboarding.join_only_waitlist", "Ask for one")}
              </button>
            )}
          </p>
        )}
        {tab === "start" && !joinOnly ? (
          showFork ? (
            <div data-testid="create-fork-card">
              <div className="font-bold text-slate-800 mb-1">{ct("onboarding.fork.title", "This email already runs a household")}</div>
              <p className="text-xs text-slate-500 mb-4">{ct("onboarding.fork.body", "You've set up Little Bankers with this address before. Creating another household starts from scratch — if you're trying to get back in, use one of these instead.")}</p>
              {recapSent ? (
                <div className="border-t border-b border-slate-200 text-xs text-slate-600 py-2.5 mb-3">
                  If that email has households on file, the code(s) are on their way — check your inbox.
                </div>
              ) : (
                <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mb-2" onClick={handleForkRecap}>{ct("onboarding.email_my_codes", "Email me my household code(s)")}</button>
              )}
              <button className="w-full bg-white border border-slate-200 text-slate-700 font-bold text-sm rounded-xl py-2.5 mb-3" onClick={handleForkRecover}>{ct("onboarding.recover_passcode", "Recover the passcode instead")}</button>
              <button className="w-full text-xs font-bold text-slate-400 underline underline-offset-2" onClick={handleForkCreateAnyway}>{ct("onboarding.create_another", "Create another household anyway")}</button>
            </div>
          ) : (
          <div>
            {/* LB-337: name first, then email. The name is the easier thing to type and the
                less loaded one to be asked for, and reading them in this order is how the
                confirmation email's greeting is assembled too. */}
            <input
              type="text"
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-3"
              placeholder={ct("onboarding.create.name_placeholder", "Your first name")}
              aria-label={ct("onboarding.create.name_placeholder", "Your first name")}
              maxLength={OWNER_NAME_MAX}
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              autoComplete="given-name"
              data-testid="create-owner-name"
            />
            <input
              type="email"
              inputMode="email"
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-3"
              placeholder="Your email address"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              autoComplete="email"
              data-testid="create-owner-email"
            />
            <p className="text-xs text-slate-400 mb-3 -mt-2">{ct("onboarding.create.email_note", "You're the household owner — we'll send a confirmation email, and your household code follows once you click it.")}</p>
            <label className="flex items-start gap-2 mb-3 text-xs text-slate-500 cursor-pointer">
              <input type="checkbox" className="mt-0.5 flex-shrink-0" checked={agreedToLegal} onChange={(e) => setAgreedToLegal(e.target.checked)} />
              <span>I agree to the <a href={publicPageHref("/terms")} className="font-bold text-slate-700 underline">Terms & Conditions</a> and <a href={publicPageHref("/privacy")} className="font-bold text-slate-700 underline">Privacy Policy</a>, on behalf of myself and any children I add.</span>
            </label>
            <button disabled={busy || checkingOwner || !nameValid || !emailValid || !agreedToLegal} className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 disabled:opacity-50" onClick={handleCreateTap} data-testid="create-household">
              {checkingOwner ? "Checking…" : busy ? "Setting up…" : "Create Household"}
            </button>
            {/* W5 — restore-into-new (P7): tucked under Create because that's what it IS —
                a create that starts from your exported file. Terms consent applies the
                same as a blank create, so the checkbox gates this too. */}
            {onRestore && (
              <label className={`block text-center text-xs font-medium underline underline-offset-2 mt-2 ${agreedToLegal && !busy ? "text-slate-500 cursor-pointer" : "text-slate-300 pointer-events-none"}`} data-testid="restore-from-backup">
                {ct("onboarding.restore_link", "Restore from a backup file")}
                <input type="file" accept="application/json,.json" className="hidden" disabled={!agreedToLegal || busy} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) onRestore(f); e.target.value = ""; }} />
              </label>
            )}
          </div>
          )
        ) : (
          <div>
                {/* Handover fallback: the QR route is useless here — it is the SAME device, so
                    there is nothing to scan from. Say what this is and where the passcode lives,
                    and be explicit that it is the browser, not this app, that has it: opening
                    a link to the same origin from inside the installed app opens inside the
                    installed app, so the words have to carry the directions. */}
                {resumedCode && (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2.5 mb-3 text-left" data-testid="join-resumed">
                    <div className="text-sm font-bold text-slate-900">{ct("onboarding.join.resumed_title", "Welcome back")}</div>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">{ct("onboarding.join.resumed_body", "To link your household to the app, we just need to confirm it's you. To find your household Passcode, open Little Bankers in the web browser, then go to Settings → Household.")}</p>
                  </div>
                )}
            {cameraSupported() && !resumedCode && (
              <>

                <p className="text-xs text-slate-400 mb-2">{ct("onboarding.join.qr_hint", "Fastest way in: on a device that's already joined, open Parent Mode → Settings → Household → Quick join to show the QR — then scan it here.")}</p>
                <button type="button" className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mb-3" onClick={() => setScanning(true)}>
                  <Icon id="ic-photo" size={16} className="inline-block align-[-3px] mr-1.5" style={{ color: "currentColor" }} />Scan QR to join
                </button>
                <div className="flex items-center gap-3 mb-3" aria-hidden="true">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs font-medium text-slate-400">or</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
              </>
            )}
            <p className="text-xs text-slate-400 mb-3">{ct("onboarding.join.code_hint", "Ask whoever set up your household for the code and passcode — both live in Parent Mode → Settings → Household.")}</p>
            <input className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-3 text-center tracking-widest font-bold uppercase" maxLength={6} placeholder="Enter 6-Digit Code" value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} />
            <div className="relative mb-3">
              <input
                className="border border-slate-200 rounded-lg pl-3 pr-16 py-2 text-sm w-full"
                type={showJoinPasscode ? "text" : "password"}
                maxLength={30}
                placeholder="Household passcode"
                value={joinPasscode}
                onChange={(e) => setJoinPasscode(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-1 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 px-2 py-1"
                onClick={() => setShowJoinPasscode((v) => !v)}
                tabIndex={-1}
              >
                {showJoinPasscode ? "Hide" : "Show"}
              </button>
            </div>
            <div className="text-right mb-3">
              <button type="button" className="text-xs font-bold text-slate-900 underline" onClick={() => setShowRecovery((v) => !v)}>
                Forgot your passcode?
              </button>
            </div>
            {showRecovery && <PasscodeRecoveryRequest code={joinCode} />}
            <button disabled={busy || joinCode.length !== 6 || joinPasscode.length === 0} className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 disabled:opacity-50" onClick={() => onJoin(joinCode, joinPasscode)}>
              {busy ? "Connecting…" : "Join Household"}
            </button>
          </div>
        )}
        {/* LB-97 — same dead end as the gate, one screen along: this card is reachable
            from the landing page's "Already have a household?" route, where the person
            may turn out to have neither a household code nor an invite. Passed ONLY on
            that route (Root.jsx) — a parent who has already spent an invite code and is
            setting their household up must never be offered the waitlist. */}
        {onWaitlist && (
          <p className="text-center mt-3">
            <button className="text-xs font-bold text-slate-900 underline underline-offset-2" data-testid="onboarding-waitlist" onClick={onWaitlist}>
              {ct("onboarding.join.no_code", "Not got a code or a household yet? Join the waitlist")}
            </button>
          </p>
        )}
      </div>
      </div>
      <CopyrightFooter />
    </div>
  );
}

// Fast-tap button for the PIN pad. Plain onClick on iOS Safari waits for the touch to fully
// resolve (touchstart → touchend → synthesized click), which is normally invisible but under
// quick repeated taps the queued click events measurably lag behind the finger — digits arrive
// late or occasionally get coalesced. Firing the digit on `pointerdown` instead reacts the
// instant a finger lands, and preventDefault there stops the browser from ever generating the
// trailing compatibility click, so there's nothing left to double up.

// The FAQ corpus that used to live here has moved to the CMS `faq` area, with a smaller
// baked fallback in src/lib/faq.js (BAKED_FAQS). See docs/HELP_SCOPE.md §2 — the reason
// is that a hardcoded corpus made every correction a deploy, which is how the Savings
// Vault answer went on describing a bank-account concept for weeks after W1.6 deleted it.
// Nothing outside this file ever imported FAQS, so the export is simply gone rather than
// left as a shim.

// Test-only alias so the harness can mount the sheet directly.
export { InstallExplainerCard as InstallExplainerCardForTest };

export { PinPadButton, PinPad, PinReset, PIN_DIGITS } from "./onboarding/Pin.jsx";
export { PasscodeReset } from "./onboarding/Recovery.jsx";
export { FaqItem, ReportForm, HelpModal } from "./onboarding/Help.jsx";
export { pushEnvironment, usePushStatus, PUSH_REG_KEY_PREFIX, requestPushSubscription, resyncPushSubscription, deniedRecoveryHint, pushUnsupportedReason, NotificationToggle, NotificationCoverageBanner, PUSH_PROMPT_MAX_ASKS, PUSH_PROMPT_SNOOZE_MS, readPromptState, writePromptState, KidNotificationPrompt, ParentNotificationPrompt } from "./onboarding/push.jsx";
export { DEVICE_ROLE_OPTIONS_NOTE, DeviceWhoseScreen, DeviceRoleConfirmScreen, NewChildVisibilityPrompt, DeviceKidsToggleScreen } from "./onboarding/DeviceRole.jsx";
export { InstallExplainerCard, useInstallPrompt, installReloadUrl, InstallInstructionModal, InstallButton } from "./onboarding/Install.jsx";
export { ProfileSwitcherSheet, ParentProfileSwitcher, KidProfileSwitcher } from "./onboarding/ProfileSwitchers.jsx";
